import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { FileText, Mail, AlertCircle, CheckCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [resetToken, setResetToken] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/forgot-password`, { email });
      setSuccess(true);
      if (response.data.reset_token) {
        setResetToken(response.data.reset_token);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao processar pedido.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#f8fafc] px-4" data-testid="forgot-password-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center mb-6">
            <img src="/evolutt_logo_dark.png" alt="Evolutt" className="h-12 w-auto" />
          </Link>
        </div>

        <Card className="shadow-lg border-0" data-testid="forgot-password-card">
          <CardHeader className="text-center pb-2">
            <CardTitle className="text-2xl font-semibold" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
              Recuperar Password
            </CardTitle>
            <p className="text-sm text-[#64748b] mt-2">
              Introduza o seu email para receber instruções de recuperação.
            </p>
          </CardHeader>
          <CardContent className="pt-6">
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700" data-testid="forgot-error">
                <AlertCircle className="w-4 h-4" />
                <span className="text-sm">{error}</span>
              </div>
            )}

            {success ? (
              <div className="space-y-4">
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg flex items-start gap-3" data-testid="forgot-success">
                  <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                  <div>
                    <p className="text-sm text-green-800 font-medium">Pedido processado com sucesso</p>
                    <p className="text-xs text-green-700 mt-1">
                      Se o email existir no sistema, receberá instruções para repor a password.
                    </p>
                  </div>
                </div>
                {resetToken && (
                  <div className="p-3 bg-[#f0f9ff] border border-[#1E3C3C]/20 rounded-lg">
                    <p className="text-xs text-[#64748b] mb-1">Link de recuperação:</p>
                    <Link 
                      to={`/reset-password?token=${resetToken}`}
                      className="text-sm text-[#1E3C3C] font-medium hover:underline break-all"
                      data-testid="reset-link"
                    >
                      Clique aqui para redefinir a password
                    </Link>
                  </div>
                )}
                <Link to="/login">
                  <Button variant="outline" className="w-full mt-2" data-testid="back-to-login-btn">
                    Voltar ao Login
                  </Button>
                </Link>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="email" className="text-sm font-medium text-[#0f172a]">Email</Label>
                  <div className="relative mt-1">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748b]" />
                    <Input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="seu@email.com"
                      className="pl-10 input-focus"
                      required
                      data-testid="forgot-email-input"
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full btn-primary py-5"
                  disabled={loading}
                  data-testid="forgot-submit-btn"
                >
                  {loading ? <div className="loading-spinner w-5 h-5"></div> : 'Enviar'}
                </Button>
              </form>
            )}

            <div className="mt-6 text-center">
              <Link to="/login" className="text-sm text-[#1E3C3C] font-medium hover:underline" data-testid="back-login-link">
                Voltar ao Login
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
